import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api from './api';
import useAuth from '../hooks/useAuth';
import { isSuper } from '../utils/canDo';

/* ── Authenticated image loader ─── */
function SecureImg({ src, alt, style, onClick }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);
  const urlRef = useRef(null);
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    api.get(src, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        const u = URL.createObjectURL(res.data);
        urlRef.current = u;
        setBlobUrl(u);
      })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [src]);
  if (err || !blobUrl) {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', color: '#bbb', fontSize: '0.75em' }}
        onClick={onClick}>
        {err ? 'Failed to load' : 'Loading…'}
      </div>
    );
  }
  return <img src={blobUrl} alt={alt} style={style} onClick={onClick} />;
}

const STYLES = `
@keyframes slideUp {
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes popIn {
  0%   { opacity:0; transform:scale(0.9); }
  70%  { transform:scale(1.03); }
  100% { opacity:1; transform:scale(1); }
}
@keyframes shimmer {
  0%   { background-position:-600px 0; }
  100% { background-position:600px 0; }
}
@keyframes qrPulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(25,118,210,0.3); }
  50%      { box-shadow: 0 0 0 14px rgba(25,118,210,0); }
}
@keyframes fadeIn {
  from { opacity:0; }
  to   { opacity:1; }
}
.ar-branch-btn { transition: all 0.18s ease; }
.ar-branch-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(25,118,210,0.3) !important; }
.ar-branch-btn.active { transform: translateY(-2px); }
.ar-row:hover { background: #f0f7ff !important; transition: background 0.15s; }
.ar-filter-input { transition: border 0.2s, box-shadow 0.2s; }
.ar-filter-input:focus { outline:none; border-color:#1976d2 !important; box-shadow:0 0 0 3px rgba(25,118,210,0.15); }
@media print {
  .no-print { display:none !important; }
  .print-only { display:block !important; }
}
`;

/* ─── Compliance issue reasons ─── */
const COMPLIANCE_REASONS = [
  'No jacket/apron',
  'Hair not tied',
  'No ID badge',
  'Other',
];

export default function AttendanceReport() {
  const { auth } = useAuth();
  const hasReviewPerm = isSuper(auth) || (auth?.permissions || []).includes('attendance.review');
  const canSeeHours = isSuper(auth) || (auth?.roles || []).includes('supervisor');

  /* ─── tabs ─── */
  const [activeTab, setActiveTab] = useState('review');
  const isPresenceViewer = (auth?.roles || []).some(r => ['owner', 'solution', 'supervisor'].includes(r));

  /* ─── filters ─── */
  const [filter, setFilter]             = useState('today');
  const [fromDate, setFromDate]         = useState('');
  const [toDate, setToDate]             = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [userFilter, setUserFilter]     = useState('');

  /* ─── data ─── */
  const [branches, setBranches] = useState([]);
  const [records, setRecords]   = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(false);

  /* ─── QR display ─── */
  const [showQrSection, setShowQrSection] = useState(false);
  const [qrBranch, setQrBranch]   = useState(null);
  const [qrLoading, setQrLoading] = useState(null);

  /* ─── review section ─── */
  const [reviewDate, setReviewDate]         = useState('');
  const [reviewRecords, setReviewRecords]   = useState([]);
  const [reviewLoading, setReviewLoading]   = useState(false);
  const [zoomImg, setZoomImg]               = useState(null);
  const [reviewingId, setReviewingId]       = useState(null);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [otherText, setOtherText]           = useState('');

  /* ─── staff presence ─── */
  const [presenceData, setPresenceData]     = useState([]);
  const [presenceLoading, setPresenceLoading] = useState(false);
  const presenceTimerRef = useRef(null);

  const fetchPresence = useCallback(async () => {
    try {
      setPresenceLoading(true);
      const res = await api.get(`${process.env.REACT_APP_URL}/myattendance/geo-presence`);
      setPresenceData(res.data || []);
    } catch (e) { setPresenceData([]); }
    finally { setPresenceLoading(false); }
  }, []);

  // Auto-refresh presence every 60s when tab is active
  useEffect(() => {
    if (activeTab === 'presence' && isPresenceViewer) {
      fetchPresence();
      presenceTimerRef.current = setInterval(fetchPresence, 60000);
    }
    return () => { clearInterval(presenceTimerRef.current); };
  }, [activeTab, isPresenceViewer, fetchPresence]);

  useEffect(() => {
    api.get(`${process.env.REACT_APP_URL}/branches`)
      .then(r => setBranches(r.data || []))
      .catch(() => {});
    fetchReport();
  }, []);

  const getDateRange = () => {
    const todayIST = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
    const fmt = d => d.toISOString().slice(0, 10);
    if (filter === 'today')  { const t = fmt(todayIST); return { from: t, to: t }; }
    if (filter === 'week')   { const s = new Date(todayIST); s.setUTCDate(todayIST.getUTCDate() - 6); return { from: fmt(s), to: fmt(todayIST) }; }
    if (filter === 'month')  { const s = new Date(todayIST.getUTCFullYear(), todayIST.getUTCMonth(), 1); return { from: fmt(s), to: fmt(todayIST) }; }
    if (filter === 'custom') return { from: fromDate, to: toDate };
    return {};
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to)   params.append('to', to);
      if (branchFilter) params.append('branch', branchFilter);
      if (userFilter)   params.append('username', userFilter.toLowerCase().trim());
      const { data } = await api.get(`${process.env.REACT_APP_URL}/myattendance/report?${params}`);
      setRecords(data.records || []);
      setSummary(data.summary || null);
    } catch (e) {
      alert('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const showQR = async (branch) => {
    if (qrBranch?.branchId === branch.branchId) { setQrBranch(null); return; }
    setQrLoading(branch.branchId);
    try {
      const { data } = await api.get(`${process.env.REACT_APP_URL}/myattendance/qr/${branch.branchId}`);
      setQrBranch({ branchId: branch.branchId, name: branch.name, token: data.token });
    } catch (err) {
      alert(err.response?.data?.error || 'Could not generate QR. Check that branch has geo coordinates (siteLat/siteLon).');
    } finally {
      setQrLoading(null);
    }
  };

  const fmtTime = iso => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtMins = m  => m != null ? `${Math.floor(m / 60)}h ${m % 60}m` : '—';
  const today   = new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);

  /* ─── review: init date + fetch on date change ─── */
  useEffect(() => { if (!reviewDate) setReviewDate(today); }, []);
  useEffect(() => {
    if (hasReviewPerm && reviewDate) fetchReviewSelfies(reviewDate);
  }, [reviewDate, hasReviewPerm]);

  const fetchReviewSelfies = async (date) => {
    setReviewLoading(true);
    try {
      const { data } = await api.get(`${process.env.REACT_APP_URL}/myattendance/selfies/${date}`);
      setReviewRecords(data || []);
    } catch (e) { setReviewRecords([]); }
    finally { setReviewLoading(false); }
  };

  const handleDeleteSelfie = async (id, username) => {
    if (!window.confirm(`Delete selfie for ${username}? They will need to re-upload.`)) return;
    try {
      await api.delete(`${process.env.REACT_APP_URL}/myattendance/selfie/${id}`);
      fetchReviewSelfies(reviewDate);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete selfie');
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`${process.env.REACT_APP_URL}/myattendance/uniform-review/${id}`, {
        status: 'approved', issues: []
      });
      fetchReviewSelfies(reviewDate);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve');
    }
  };

  const handleFlagIssue = (id) => {
    setReviewingId(id);
    setSelectedIssues([]);
    setOtherText('');
  };

  const submitIssue = async () => {
    const issues = [...selectedIssues];
    if (issues.includes('Other') && otherText.trim()) {
      issues[issues.indexOf('Other')] = otherText.trim();
    } else if (issues.includes('Other')) {
      issues.splice(issues.indexOf('Other'), 1);
    }
    if (issues.length === 0) return alert('Please select at least one issue.');
    try {
      await api.post(`${process.env.REACT_APP_URL}/myattendance/uniform-review/${reviewingId}`, {
        status: 'issue', issues
      });
      setReviewingId(null);
      fetchReviewSelfies(reviewDate);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit');
    }
  };

  const inputStyle = {
    padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0',
    fontSize: '0.88em', background: '#fafafa',
  };

  /* ─────────────────── RENDER ─────────────────── */
  return (
    <>
      <style>{STYLES}</style>
      <div style={{ padding: '20px 16px', fontFamily: "'Segoe UI',sans-serif", maxWidth: 1200, margin: '0 auto' }}>

        {/* ══ PAGE HEADER ══ */}
        <div className="no-print" style={{ marginBottom: hasReviewPerm ? 10 : 24, animation: 'slideUp 0.4s ease' }}>
          <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: '#1a237e' }}>
            📋 {activeTab === 'review' ? 'Review Attendance' : 'Attendance Report'}
          </h2>
          <div style={{ color: '#888', fontSize: '0.85em', marginTop: 4 }}>{today}</div>
        </div>

        {/* ══ TAB BAR (only if user has review access) ══ */}
        {hasReviewPerm && (
          <div className="no-print" style={{ display: 'flex', gap: 0, marginBottom: 20, borderRadius: 10, overflow: 'hidden', border: '1.5px solid #c5cae9' }}>
            {[
              { key: 'review', label: 'Review Attendance' },
              { key: 'report', label: 'Attendance Report' },
              ...(isPresenceViewer ? [{ key: 'presence', label: 'Staff Presence' }] : []),
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer',
                  fontWeight: 700, fontSize: '0.85em',
                  background: activeTab === t.key ? '#1a237e' : '#fff',
                  color: activeTab === t.key ? '#fff' : '#1a237e',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* ══ REPORT TAB CONTENT ══ */}
        {activeTab === 'report' && (<>

        {/* ══ BRANCH QR SECTION ══ */}
        <div className="no-print" style={{ marginBottom: 24 }}>
          <button
            onClick={() => { setShowQrSection(v => !v); if (showQrSection) setQrBranch(null); }}
            style={{
              padding: '10px 22px', borderRadius: 12, border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: '0.9em',
              background: showQrSection
                ? 'linear-gradient(135deg,#1976d2,#0d47a1)'
                : 'linear-gradient(135deg,#e8eaf6,#f3f4ff)',
              color: showQrSection ? '#fff' : '#1976d2',
              boxShadow: showQrSection ? '0 6px 18px rgba(25,118,210,0.4)' : '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.2s',
            }}
          >
            {showQrSection ? '✕ Hide QR Codes' : '📷 Show QR Codes'}
          </button>
        </div>

        {showQrSection && (
        <section className="no-print" style={{
          background: 'linear-gradient(135deg,#e8eaf6,#f3f4ff)',
          borderRadius: 18, padding: '20px 20px 24px',
          marginBottom: 24,
          boxShadow: '0 8px 28px rgba(63,81,181,0.12)',
          animation: 'slideUp 0.4s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: '1.5rem' }}>🏢</span>
            <h3 style={{ margin: 0, color: '#1a237e', fontWeight: 700 }}>Branch QR Codes</h3>
          </div>
          <p style={{ color: '#5c6bc0', margin: '0 0 16px', fontSize: '0.85em' }}>
            Click a branch to generate its daily QR. Display on screen or print for employees to scan.
            QR auto-refreshes every 24 hours.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {branches.filter(b => b.isActive !== false && b.status !== 'inactive').map(b => {
              const isActive = qrBranch?.branchId === b.branchId;
              const isLoading = qrLoading === b.branchId;
              return (
                <button
                  key={b.branchId}
                  className={`ar-branch-btn${isActive ? ' active' : ''}`}
                  onClick={() => showQR(b)}
                  disabled={isLoading}
                  style={{
                    padding: '9px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    fontWeight: 700, fontSize: '0.88em', letterSpacing: 0.3,
                    background: isActive
                      ? 'linear-gradient(135deg,#1976d2,#0d47a1)'
                      : 'rgba(255,255,255,0.85)',
                    color: isActive ? '#fff' : '#1976d2',
                    boxShadow: isActive
                      ? '0 6px 18px rgba(25,118,210,0.4)'
                      : '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  {isLoading ? '⏳' : isActive ? '✅' : '📷'} {b.name || b.branchId}
                </button>
              );
            })}
          </div>

          {qrBranch && (
            <div style={{
              marginTop: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
              animation: 'popIn 0.35s ease',
            }}>
              <div style={{
                background: '#fff', padding: '28px 32px', borderRadius: 20,
                boxShadow: '0 16px 48px rgba(0,0,0,0.18)', textAlign: 'center',
                animation: 'qrPulse 3s infinite',
              }}>
                <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#1a237e', marginBottom: 4 }}>
                  {qrBranch.name}
                </div>
                <div style={{ color: '#888', fontSize: '0.8em', marginBottom: 16 }}>{today} · Valid 24 hours</div>
                <div className="qr-print-area"><QRCodeSVG value={qrBranch.token} size={280} level="H" includeMargin /></div>
                <div style={{ marginTop: 12, color: '#aaa', fontSize: '0.75em' }}>
                  Scan with Attendance app
                </div>
              </div>
              <button
                onClick={() => {
                  const svgEl = document.querySelector('.qr-print-area svg');
                  if (!svgEl) { alert('QR not found'); return; }
                  const svgData = new XMLSerializer().serializeToString(svgEl);
                  const w = window.open('', '_blank', 'width=500,height=600');
                  w.document.write(`<html><head><title>QR - ${qrBranch.name}</title>
                    <style>body{margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:sans-serif;}
                    h2{margin:0 0 4px;color:#1a237e;} .sub{color:#888;font-size:13px;margin-bottom:18px;} .foot{color:#aaa;font-size:12px;margin-top:14px;}</style>
                    </head><body>
                    <h2>${qrBranch.name}</h2>
                    <div class="sub">${today} · Valid 24 hours</div>
                    ${svgData}
                    <div class="foot">Scan with Attendance app</div>
                    </body></html>`);
                  w.document.close();
                  setTimeout(() => { w.print(); }, 400);
                }}
                style={{
                  padding: '10px 28px', borderRadius: 10, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg,#43a047,#1b5e20)',
                  color: '#fff', fontWeight: 700, fontSize: '0.9em',
                  boxShadow: '0 6px 18px rgba(67,160,71,0.35)',
                }}
              >
                🖨️ Print QR
              </button>
            </div>
          )}
        </section>
        )}

        {/* ══ FILTERS ══ */}
        <section className="no-print" style={{
          background: '#fff', borderRadius: 16, padding: '18px 20px',
          marginBottom: 22,
          boxShadow: '0 4px 18px rgba(0,0,0,0.07)',
          animation: 'slideUp 0.45s ease',
        }}>
          <h3 style={{ margin: '0 0 14px', color: '#333', fontWeight: 700 }}>🔍 Filters</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '0.72em', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Period</label>
              <select
                value={filter} onChange={e => setFilter(e.target.value)}
                className="ar-filter-input" style={inputStyle}
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {filter === 'custom' && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: '0.72em', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>From</label>
                  <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                    className="ar-filter-input" style={inputStyle} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: '0.72em', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>To</label>
                  <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                    className="ar-filter-input" style={inputStyle} />
                </div>
              </>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '0.72em', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Branch</label>
              <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)}
                className="ar-filter-input" style={inputStyle}>
                <option value="">All Branches</option>
                {branches.map(b => <option key={b.branchId} value={b.branchId}>{b.name}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '0.72em', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Employee</label>
              <input type="text" placeholder="username" value={userFilter}
                onChange={e => setUserFilter(e.target.value)}
                className="ar-filter-input" style={{ ...inputStyle, width: 140 }} />
            </div>

            <button
              onClick={fetchReport}
              disabled={loading}
              style={{
                padding: '9px 22px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: loading
                  ? '#ccc'
                  : 'linear-gradient(135deg,#1976d2,#0d47a1)',
                color: '#fff', fontWeight: 700, fontSize: '0.9em',
                boxShadow: loading ? 'none' : '0 4px 14px rgba(25,118,210,0.35)',
                alignSelf: 'flex-end',
                transition: 'all 0.2s',
              }}
            >
              {loading ? '⏳ Loading…' : '🔄 Refresh'}
            </button>
          </div>
        </section>

        {/* ══ SUMMARY 3D CARDS ══ */}
        {summary && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 14, marginBottom: 24,
            animation: 'slideUp 0.5s ease',
          }}>
            {[
              { label: 'Total',       value: summary.total,      color: '#1976d2', icon: '📊', bg: 'linear-gradient(135deg,#e3f2fd,#bbdefb)' },
              { label: 'Present',     value: summary.present,    color: '#43a047', icon: '✅', bg: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)' },
              { label: 'Completed',   value: summary.completed,  color: '#2e7d32', icon: '🏁', bg: 'linear-gradient(135deg,#f1f8e9,#dcedc8)' },
              { label: 'In Progress', value: summary.inProgress, color: '#f57c00', icon: '🔵', bg: 'linear-gradient(135deg,#fff8e1,#ffe082)' },
              { label: 'Sunday OT',   value: summary.sundayOT,   color: '#7b1fa2', icon: '☀️', bg: 'linear-gradient(135deg,#f3e5f5,#ce93d8)' },
            ].map((c, i) => (
              <div key={c.label} style={{
                background: c.bg,
                borderRadius: 16, padding: '18px 14px', textAlign: 'center',
                boxShadow: `0 8px 24px rgba(0,0,0,0.09), inset 0 -4px 0 ${c.color}33`,
                transform: 'perspective(600px) rotateX(2deg)',
                animation: `slideUp ${0.4 + i * 0.07}s ease`,
                transition: 'transform 0.2s',
                cursor: 'default',
              }}
                onMouseEnter={e => e.currentTarget.style.transform='perspective(600px) rotateX(0deg) translateY(-4px)'}
                onMouseLeave={e => e.currentTarget.style.transform='perspective(600px) rotateX(2deg)'}
              >
                <div style={{ fontSize: '1.6rem', marginBottom: 4 }}>{c.icon}</div>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: '0.72em', color: '#666', marginTop: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* ══ RECORDS TABLE ══ */}
        <section style={{ animation: 'slideUp 0.55s ease' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, color: '#333', fontWeight: 700 }}>
              Records
              <span style={{
                marginLeft: 8, background: '#1976d2', color: '#fff',
                borderRadius: 20, padding: '2px 10px', fontSize: '0.7em', fontWeight: 700, verticalAlign: 'middle'
              }}>{records.length}</span>
            </h3>
          </div>

          {loading ? (
            /* Skeleton rows */
            <div style={{ borderRadius: 14, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.07)' }}>
              {[1,2,3,4].map(i => (
                <div key={i} style={{
                  height: 44, margin: 4, borderRadius: 8,
                  background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)',
                  backgroundSize: '800px 100%', animation: 'shimmer 1.4s infinite linear',
                }} />
              ))}
            </div>
          ) : records.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '48px 20px', borderRadius: 14,
              background: '#fafafa', border: '2px dashed #e0e0e0', color: '#bbb',
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🗂️</div>
              <div style={{ fontWeight: 600 }}>No records found</div>
              <div style={{ fontSize: '0.82em', marginTop: 4 }}>Try changing the filters above</div>
            </div>
          ) : (
            <div style={{
              borderRadius: 14, overflow: 'hidden',
              boxShadow: '0 6px 24px rgba(0,0,0,0.09)',
              overflowX: 'auto',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.87em', minWidth: 960 }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg,#1a237e,#283593)' }}>
                    {['Date','Employee','Emp ID','Check-in','Selfie','Check-out',canSeeHours && 'Worked',canSeeHours && 'Not Available','Status','Selfie Status','OT'].filter(Boolean).map(h => (
                      <th key={h} style={{
                        padding: '11px 12px', textAlign: 'left', color: '#fff',
                        fontWeight: 700, fontSize: '0.8em', textTransform: 'uppercase',
                        letterSpacing: 0.6, whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, idx) => {
                    const shifts = r.shifts || [];
                    const hasMultiShifts = shifts.length > 1;
                    return (
                    <React.Fragment key={r._id}>
                    <tr
                      className="ar-row"
                      style={{
                        borderBottom: hasMultiShifts ? 'none' : '1px solid #f0f0f0',
                        background: r.isSunday ? '#fff8e1' : idx % 2 === 0 ? '#fff' : '#fafbff',
                      }}
                    >
                      <td style={{ padding: '9px 12px', fontWeight: 600, color: '#333' }}>
                        {r.date}
                        {hasMultiShifts && <div style={{ fontSize: '0.7em', color: '#7c3aed', fontWeight: 700 }}>{shifts.length} shifts</div>}
                      </td>
                      <td style={{ padding: '9px 12px', fontWeight: 700, color: '#1a237e' }}>{r.username}</td>
                      <td style={{ padding: '9px 12px', color: '#555' }}>{r.employeeId || r.username}</td>
                      <td style={{ padding: '9px 12px', color: '#43a047', fontWeight: 600 }}>{fmtTime(r.checkInTime)}</td>
                      <td style={{ padding: '9px 12px', color: '#7b1fa2', fontWeight: 600 }}>{fmtTime(r.selfieAt)}</td>
                      <td style={{ padding: '9px 12px', color: '#e53935', fontWeight: 600 }}>{fmtTime(r.checkOutTime)}</td>
                      {canSeeHours && <td style={{ padding: '9px 12px', fontWeight: r.workedMinutes != null ? 700 : 400, color: '#1976d2' }}>{fmtMins(r.workedMinutes)}</td>}
                      {canSeeHours && <td style={{ padding: '9px 12px', fontWeight: 600, color: r.outsideMinutes > 0 ? '#ef5350' : '#bbb' }}>{r.outsideMinutes > 0 ? fmtMins(r.outsideMinutes) : '—'}</td>}
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 10px', borderRadius: 20, fontSize: '0.8em', fontWeight: 700,
                          background: r.status === 'COMPLETED' ? '#e8f5e9' : r.status === 'IN_PROGRESS' ? '#e3f2fd' : '#f5f5f5',
                          color: r.status === 'COMPLETED' ? '#2e7d32' : r.status === 'IN_PROGRESS' ? '#1565c0' : '#888',
                        }}>
                          {r.status === 'COMPLETED' ? '✅ Done' : r.status === 'IN_PROGRESS' ? '🔵 Active' : r.status}
                        </span>
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        {(() => {
                          const s = r.uniformStatus;
                          const approvalInfo = r.uniformReviewedAt ? <div style={{ fontSize: '0.65em', color: '#888', marginTop: 2 }}>{fmtTime(r.uniformReviewedAt)}{r.uniformReviewedBy ? ` — ${r.uniformReviewedBy}` : ''}</div> : null;
                          if (s === 'approved') return <div><span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 10, padding: '2px 8px', fontSize: '0.8em', fontWeight: 700 }}>✅ OK</span>{approvalInfo}</div>;
                          if (s === 'issue') return <div><span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 10, padding: '2px 8px', fontSize: '0.8em', fontWeight: 700 }}>⚠️ Issue</span>{approvalInfo}</div>;
                          if (s === 'reupload') return <div><span style={{ background: '#fef3c7', color: '#d97706', borderRadius: 10, padding: '2px 8px', fontSize: '0.8em', fontWeight: 700 }}>🔄 Re-upload</span>{approvalInfo}</div>;
                          if (s === 'pending') return <span style={{ background: '#fff8e1', color: '#f57c00', borderRadius: 10, padding: '2px 8px', fontSize: '0.8em', fontWeight: 700 }}>⏳ Pending</span>;
                          return <span style={{ color: '#bbb' }}>—</span>;
                        })()}
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        {r.isSunday
                          ? <span style={{ background: '#fff3e0', color: '#e65100', borderRadius: 10, padding: '2px 8px', fontSize: '0.8em', fontWeight: 700 }}>☀️ OT</span>
                          : <span style={{ color: '#bbb' }}>—</span>}
                      </td>
                    </tr>
                    {/* Individual shift rows for multi-shift days */}
                    {hasMultiShifts && shifts.map((s, si) => (
                      <tr key={`${r._id}-s${si}`} style={{
                        borderBottom: si === shifts.length - 1 ? '1px solid #f0f0f0' : '1px solid #f5f5f5',
                        background: r.isSunday ? '#fffde7' : idx % 2 === 0 ? '#f8f9ff' : '#f3f4fa',
                      }}>
                        <td style={{ padding: '5px 12px 5px 24px', fontSize: '0.78em', color: '#7c3aed', fontWeight: 600 }}>Shift {si + 1}</td>
                        <td colSpan={2} style={{ padding: '5px 12px', fontSize: '0.78em', color: '#888' }}></td>
                        <td style={{ padding: '5px 12px', fontSize: '0.82em', color: '#43a047', fontWeight: 600 }}>{fmtTime(s.checkIn)}</td>
                        <td style={{ padding: '5px 12px', fontSize: '0.82em', color: '#bbb' }}>—</td>
                        <td style={{ padding: '5px 12px', fontSize: '0.82em', color: s.checkOut ? '#e53935' : '#bbb', fontWeight: 600 }}>{s.checkOut ? fmtTime(s.checkOut) : 'ongoing'}</td>
                        {canSeeHours && <td style={{ padding: '5px 12px', fontSize: '0.82em', color: '#1976d2', fontWeight: 600 }}>{s.minutes != null ? fmtMins(s.minutes) : '...'}</td>}
                        {canSeeHours && <td style={{ padding: '5px 12px' }}></td>}
                        <td style={{ padding: '5px 12px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.72em', fontWeight: 700,
                            background: s.checkOut ? '#e8f5e9' : '#e3f2fd',
                            color: s.checkOut ? '#2e7d32' : '#1565c0',
                          }}>
                            {s.checkOut ? 'Done' : 'Active'}
                          </span>
                        </td>
                        <td style={{ padding: '5px 12px' }}></td>
                        <td style={{ padding: '5px 12px' }}></td>
                      </tr>
                    ))}
                    {/* Geo-exit rows for location changes */}
                    {(r.geoExits || []).length > 0 && (r.geoExits || []).map((ex, ei) => (
                      <tr key={`${r._id}-geo${ei}`} style={{
                        borderBottom: ei === r.geoExits.length - 1 && !hasMultiShifts ? '1px solid #f0f0f0' : '1px solid #f5f5f5',
                        background: '#fef2f2',
                      }}>
                        <td style={{ padding: '4px 12px 4px 24px', fontSize: '0.75em', color: '#dc2626', fontWeight: 600 }}>
                          Exit {ei + 1}
                        </td>
                        <td colSpan={2} style={{ padding: '4px 12px', fontSize: '0.75em', color: '#dc2626' }}>Not at centre</td>
                        <td style={{ padding: '4px 12px', fontSize: '0.78em', color: '#dc2626' }}>{fmtTime(ex.exitAt)}</td>
                        <td style={{ padding: '4px 12px' }}></td>
                        <td style={{ padding: '4px 12px', fontSize: '0.78em', color: ex.entryAt ? '#43a047' : '#dc2626' }}>
                          {ex.entryAt ? fmtTime(ex.entryAt) : 'still out'}
                        </td>
                        {canSeeHours && <td style={{ padding: '4px 12px', fontSize: '0.78em', color: '#dc2626', fontWeight: 600 }}>
                          {ex.entryAt ? fmtMins(Math.round((new Date(ex.entryAt) - new Date(ex.exitAt)) / 60000)) : '...'}
                        </td>}
                        {canSeeHours && <td style={{ padding: '4px 12px' }}></td>}
                        <td style={{ padding: '4px 12px' }}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.72em', fontWeight: 700, background: '#fef2f2', color: '#dc2626' }}>
                            Outside
                          </span>
                        </td>
                        <td colSpan={2} style={{ padding: '4px 12px' }}></td>
                      </tr>
                    ))}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        </>)}

        {/* ══════════════════════════════════════════════════════════
           REVIEW ATTENDANCE TAB
        ══════════════════════════════════════════════════════════ */}
        {activeTab === 'review' && (
          <div style={{
            background: '#fff', borderRadius: 18, padding: '18px 16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0',
            animation: 'slideUp 0.4s ease',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1a237e' }}>
                Uniform Review
              </h3>
              <input
                type="date"
                value={reviewDate}
                onChange={e => setReviewDate(e.target.value)}
                style={{
                  padding: '6px 10px', borderRadius: 8, border: '1.5px solid #d1d5db',
                  fontSize: '0.85em', color: '#333',
                }}
              />
            </div>

            {reviewLoading ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#888' }}>Loading selfies...</div>
            ) : reviewRecords.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#999', fontSize: '0.9em' }}>
                No selfies found for this date.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                {reviewRecords.map(rec => (
                  <div key={rec._id} style={{
                    background: '#f8fafc', borderRadius: 14, overflow: 'hidden',
                    border: `2px solid ${rec.uniformStatus === 'approved' ? '#43a047' : rec.uniformStatus === 'issue' ? '#ef5350' : '#e2e8f0'}`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}>
                    <div
                      onClick={() => setZoomImg(`${process.env.REACT_APP_URL}/myattendance/selfie-image/${rec.date}/${rec.username}`)}
                      style={{ cursor: 'pointer', position: 'relative' }}
                    >
                      <SecureImg
                        src={`${process.env.REACT_APP_URL}/myattendance/selfie-image/${rec.date}/${rec.username}`}
                        alt={rec.username}
                        style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                      />
                      <span style={{
                        position: 'absolute', top: 6, right: 6,
                        padding: '2px 8px', borderRadius: 10, fontSize: '0.65em', fontWeight: 700,
                        background: rec.uniformStatus === 'approved' ? '#43a047' : rec.uniformStatus === 'issue' ? '#ef5350' : '#ff9800',
                        color: '#fff',
                      }}>
                        {rec.uniformStatus === 'approved' ? 'Approved' : rec.uniformStatus === 'issue' ? 'Issue' : 'Pending'}
                      </span>
                    </div>

                    <div style={{ padding: '8px 10px' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.82em', color: '#333', marginBottom: 2 }}>
                        {rec.username}
                      </div>
                      <div style={{ fontSize: '0.72em', color: '#888' }}>
                        In: {fmtTime(rec.checkInTime)}
                      </div>
                      {rec.uniformReviewedAt && (
                        <div style={{ fontSize: '0.68em', color: rec.uniformStatus === 'approved' ? '#2e7d32' : '#d97706', marginTop: 2 }}>
                          {rec.uniformStatus === 'approved' ? 'Approved' : 'Reviewed'}: {fmtTime(rec.uniformReviewedAt)}{rec.uniformReviewedBy ? ` by ${rec.uniformReviewedBy}` : ''}
                        </div>
                      )}
                      {rec.uniformStatus === 'issue' && rec.uniformIssues?.length > 0 && (
                        <div style={{ fontSize: '0.68em', color: '#ef5350', marginTop: 3 }}>
                          {rec.uniformIssues.join(', ')}
                        </div>
                      )}

                      {rec.uniformStatus === 'pending' && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <button
                            onClick={() => handleApprove(rec._id)}
                            style={{
                              flex: 1, padding: '6px', borderRadius: 8, border: 'none',
                              background: '#43a047', color: '#fff', fontWeight: 700,
                              fontSize: '0.75em', cursor: 'pointer',
                            }}
                          >
                            {'\u2713'} Approve
                          </button>
                          <button
                            onClick={() => handleFlagIssue(rec._id)}
                            style={{
                              flex: 1, padding: '6px', borderRadius: 8, border: 'none',
                              background: '#ef5350', color: '#fff', fontWeight: 700,
                              fontSize: '0.75em', cursor: 'pointer',
                            }}
                          >
                            {'\u2715'} Issue
                          </button>
                        </div>
                      )}
                      {rec.uniformStatus !== 'approved' && (
                        <button
                          onClick={() => handleDeleteSelfie(rec._id, rec.username)}
                          style={{
                            width: '100%', padding: '5px', borderRadius: 8, border: '1px solid #e0e0e0',
                            background: '#fff', color: '#888', fontWeight: 600,
                            fontSize: '0.7em', cursor: 'pointer', marginTop: 6,
                          }}
                        >
                          {'\uD83D\uDD04'} Request Re-upload
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ STAFF PRESENCE TAB ══ */}
        {activeTab === 'presence' && isPresenceViewer && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a237e' }}>
                Live Staff Presence
              </h3>
              <button
                onClick={fetchPresence}
                disabled={presenceLoading}
                style={{
                  padding: '6px 14px', borderRadius: 8, border: '1.5px solid #c5cae9',
                  background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.8em', color: '#1a237e',
                }}
              >
                {presenceLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            <div style={{ fontSize: '0.75em', color: '#888', marginBottom: 12 }}>
              Auto-refreshes every 60 seconds. Showing staff currently checked in.
            </div>

            {presenceData.length === 0 && !presenceLoading && (
              <div style={{ textAlign: 'center', padding: 40, color: '#999', fontSize: '0.95em' }}>
                No staff currently checked in.
              </div>
            )}

            {presenceData.length > 0 && (
              <div style={{ display: 'grid', gap: 10 }}>
                {presenceData.map(p => {
                  const mins = p.workedMinutes || 0;
                  const hh = Math.floor(mins / 60);
                  const mm = mins % 60;
                  const pingAge = p.lastGeoPingAt ? Math.floor((Date.now() - new Date(p.lastGeoPingAt).getTime()) / 60000) : null;
                  const pingStale = pingAge !== null && pingAge > 30; // no ping in 30+ min = stale
                  const geoStatus = pingStale ? 'No Signal'
                    : p.inGeo === true ? 'At Hospital'
                    : p.inGeo === false ? 'Outside'
                    : 'Unknown';
                  const statusColor = pingStale ? '#ff9800'
                    : p.inGeo === true ? '#43a047'
                    : p.inGeo === false ? '#ef5350'
                    : '#9e9e9e';
                  const pingLabel = pingAge === null ? null
                    : pingAge < 1 ? 'just now'
                    : pingAge < 60 ? `${pingAge}m ago`
                    : `${Math.floor(pingAge / 60)}h ${pingAge % 60}m ago`;

                  return (
                    <div key={p.username} style={{
                      background: '#fff', borderRadius: 12, border: '1.5px solid #e8eaf6',
                      padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    }}>
                      {/* Status dot */}
                      <div style={{
                        width: 12, height: 12, borderRadius: '50%', background: statusColor,
                        flexShrink: 0, boxShadow: `0 0 6px ${statusColor}55`,
                      }} />

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9em', color: '#1a237e' }}>
                          {p.name || p.username}
                        </div>
                        <div style={{ fontSize: '0.75em', color: '#666', marginTop: 3 }}>
                          In: {p.checkInTime ? new Date(p.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                          {p.shiftsCount > 1 && <span style={{ color: '#1976d2', fontWeight: 600 }}> ({p.shiftsCount} shifts)</span>}
                          <span style={{ margin: '0 6px', color: '#ccc' }}>|</span>
                          Worked: {hh}h {mm}m
                          {p.outsideMinutes > 0 && (
                            <>
                              <span style={{ margin: '0 6px', color: '#ccc' }}>|</span>
                              <span style={{ color: '#ef5350', fontWeight: 600 }}>Away: {Math.floor(p.outsideMinutes / 60)}h {p.outsideMinutes % 60}m</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Geo status badge */}
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: 8,
                          fontSize: '0.72em', fontWeight: 700,
                          background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}40`,
                        }}>
                          {geoStatus}
                        </div>
                        {pingLabel !== null && (
                          <div style={{ fontSize: '0.65em', color: pingStale ? '#ff9800' : '#999', marginTop: 3 }}>
                            Ping: {pingLabel}
                          </div>
                        )}
                        {p.consecutiveOutside >= 4 && (
                          <div style={{ fontSize: '0.65em', color: '#ef5350', fontWeight: 700, marginTop: 2 }}>
                            Outside {p.consecutiveOutside} pings
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ COMPLIANCE ISSUE MODAL ══ */}
        {reviewingId && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}>
            <div style={{
              background: '#fff', borderRadius: 16, padding: '24px 20px', maxWidth: 360,
              width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              animation: 'popIn 0.3s ease',
            }}>
              <h3 style={{ margin: '0 0 14px', fontSize: '1rem', fontWeight: 700, color: '#c62828' }}>
                Flag Compliance Issue
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                {COMPLIANCE_REASONS.map(reason => (
                  <label key={reason} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9em', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedIssues.includes(reason)}
                      onChange={e => {
                        if (e.target.checked) setSelectedIssues(p => [...p, reason]);
                        else setSelectedIssues(p => p.filter(r => r !== reason));
                      }}
                      style={{ width: 18, height: 18, accentColor: '#ef5350' }}
                    />
                    {reason}
                  </label>
                ))}
              </div>
              {selectedIssues.includes('Other') && (
                <input
                  type="text"
                  placeholder="Describe the issue..."
                  value={otherText}
                  onChange={e => setOtherText(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #d1d5db',
                    fontSize: '0.88em', marginBottom: 14, boxSizing: 'border-box',
                  }}
                />
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setReviewingId(null)}
                  style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitIssue}
                  style={{
                    padding: '8px 20px', borderRadius: 8, border: 'none',
                    background: '#ef5350', color: '#fff', fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ ZOOM POPUP (full-size selfie) ══ */}
        {zoomImg && (
          <div
            onClick={() => setZoomImg(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
              cursor: 'pointer',
            }}
          >
            <SecureImg
              src={zoomImg}
              alt="Selfie zoomed"
              style={{
                maxWidth: '92vw', maxHeight: '85vh', borderRadius: 12, objectFit: 'contain',
                boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
              }}
            />
            <div style={{
              position: 'absolute', top: 20, right: 20, color: '#fff', fontSize: '1.5rem',
              background: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}>
              {'\u2715'}
            </div>
          </div>
        )}

      </div>
    </>
  );
}
